(() => {
  "use strict";

  const CONFIG = Object.freeze({
    opprefCookieName: "ecpr_openai_oppref",
    measurementAllowedAttribute: "__openai_measurement_allowed",
    opprefAttribute: "__openai_oppref",
    maxOpprefLength: 2048,
    retryDelaysMs: [0, 250, 1000, 3000, 10000],
  });

  let syncInFlight = null;
  let lastSyncedSignature = null;

  function validOppref(value) {
    return Boolean(value) && value.length <= CONFIG.maxOpprefLength && !/\s/.test(value);
  }

  function opprefFromCurrentUrl() {
    try {
      const parameters = new URLSearchParams(window.location.search);
      if (!parameters.has("oppref")) return undefined;

      const value = parameters.get("oppref");
      return validOppref(value) ? value : null;
    } catch (_error) {
      return undefined;
    }
  }

  function opprefFromCookie() {
    const prefix = `${CONFIG.opprefCookieName}=`;
    const cookie = document.cookie
      .split(";")
      .map((part) => part.trim())
      .find((part) => part.startsWith(prefix));

    if (!cookie) return null;

    try {
      const value = decodeURIComponent(cookie.slice(prefix.length));
      return validOppref(value) ? value : null;
    } catch (_error) {
      return null;
    }
  }

  function privacyAllowsMeasurement() {
    const privacy = window.Shopify?.customerPrivacy;

    try {
      return (
        privacy?.analyticsProcessingAllowed?.() === true &&
        privacy?.marketingAllowed?.() === true &&
        privacy?.saleOfDataAllowed?.() === true
      );
    } catch (_error) {
      return false;
    }
  }

  function desiredPrivateAttributes() {
    if (!privacyAllowsMeasurement()) {
      return {
        [CONFIG.measurementAllowedAttribute]: "",
        [CONFIG.opprefAttribute]: "",
      };
    }

    const landingOppref = opprefFromCurrentUrl();
    const oppref = landingOppref === undefined ? opprefFromCookie() : landingOppref;

    const measurementAllowed = validOppref(oppref);

    return {
      [CONFIG.measurementAllowedAttribute]: measurementAllowed ? "true" : "",
      [CONFIG.opprefAttribute]: measurementAllowed ? oppref : "",
    };
  }

  function stateSignature(attributes) {
    return JSON.stringify(attributes);
  }

  function cartUpdateUrl() {
    const root = window.Shopify?.routes?.root || "/";
    return `${root.endsWith("/") ? root : `${root}/`}cart/update.js`;
  }

  async function syncPrivateCartAttributes({ force = false } = {}) {
    const attributes = desiredPrivateAttributes();
    const signature = stateSignature(attributes);

    if (!force && lastSyncedSignature === signature) return;
    if (syncInFlight) await syncInFlight;
    if (!force && lastSyncedSignature === signature) return;

    syncInFlight = fetch(cartUpdateUrl(), {
      method: "POST",
      credentials: "same-origin",
      keepalive: true,
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ attributes }),
    })
      .then((response) => {
        if (!response.ok) throw new Error(`Cart attribution sync failed with HTTP ${response.status}.`);
        lastSyncedSignature = signature;
      })
      .catch(() => {
        // Fail closed. A later page load, consent event, or scheduled retry can try again.
      })
      .finally(() => {
        syncInFlight = null;
      });

    await syncInFlight;
  }

  function scheduleSyncs(delays = CONFIG.retryDelaysMs) {
    delays.forEach((delay) => {
      window.setTimeout(() => void syncPrivateCartAttributes(), delay);
    });
  }

  document.addEventListener("visitorConsentCollected", () => {
    scheduleSyncs([0, 250, 1000]);
  });

  window.addEventListener("pageshow", () => void syncPrivateCartAttributes());
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") void syncPrivateCartAttributes({ force: true });
  });

  if (typeof subscribe === "function" && typeof PUB_SUB_EVENTS !== "undefined") {
    subscribe(PUB_SUB_EVENTS.cartItemAdd, () => void syncPrivateCartAttributes({ force: true }));
    subscribe(PUB_SUB_EVENTS.cartUpdate, () => void syncPrivateCartAttributes({ force: true }));
  }

  scheduleSyncs();
})();
